import React from 'react';
import { useTranslation } from 'react-i18next';
import StatusChip from '../design/StatusChip';
import EmptyState from '../design/EmptyState';
import AudioButton from './AudioButton';
import { formatPasukRef, statusFromScore } from '../utils/pasuk';
import type { Recording } from '../types';

interface RecordingsListProps {
  recordings: Recording[];
  /** Show which pasuk each recording is for (teacher view across pesukim). */
  showPasuk?: boolean;
  /** Show the AI explanation under each recording. */
  showExplanation?: boolean;
}

const RecordingsList: React.FC<RecordingsListProps> = ({
  recordings,
  showPasuk = false,
  showExplanation = false,
}) => {
  const { t, i18n } = useTranslation();

  if (recordings.length === 0) {
    return <EmptyState icon="mic" title={t('school_no_recordings')} />;
  }

  const formatDate = (r: Recording): string =>
    r.createdAt
      ? r.createdAt.toDate().toLocaleString(i18n.language, {
          day: 'numeric',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit',
        })
      : '';

  return (
    <ul className="divide-y divide-school-line">
      {recordings.map((r) => (
        <li key={r.id} className="flex flex-col gap-2 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              {showPasuk && (
                <div className="font-semibold text-school-ink">
                  {formatPasukRef(r.pasuk, i18n.language)}
                </div>
              )}
              <div className="text-xs text-school-muted">{formatDate(r)}</div>
            </div>
            <div className="flex items-center gap-3">
              <StatusChip status={statusFromScore(r.score, 1)} suffix={String(r.score)} />
              <AudioButton src={r.audioUrl} label={t('school_listen')} variant="ghost" />
            </div>
          </div>
          {showExplanation && r.explanation && (
            <p className="whitespace-pre-line rounded-xl bg-school-canvas p-3 text-sm text-school-ink">
              {r.explanation}
            </p>
          )}
        </li>
      ))}
    </ul>
  );
};

export default RecordingsList;
